/**
 * Parwaaz — tenant configuration loading and derived values.
 * `config/tenant.json` is the single source of truth for a deployment (office identity,
 * season dates, theme, ruleset thresholds, signing keys). `loadTenant` validates the shape
 * so a mistyped config fails loudly at boot instead of silently mis-validating applications.
 */
import { addDays } from './dates';
import type { ISODate, TenantConfig } from './types';

/** Sensible starting values for the season-independent parts of a new tenant config. */
export const TENANT_DEFAULTS = {
  product: {
    languages: ['en', 'hi'],
    defaultLanguage: 'en',
  },
  season: {
    cardValidityDays: 0, // 0 = card valid through the end of the season
  },
  rules: {
    rulesetVersion: '1.0.0',
    nameMatchPass: 0.9,
    nameMatchWarn: 0.75,
  },
  retention: {
    applicationYears: 7,
    documentYearsAfterExpiry: 3,
    scanLogYears: 2,
  },
  verify: {
    staleAfterHours: 24,
  },
} as const;

type Check = { path: string; test: (v: unknown) => boolean };

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}
function isString(v: unknown): v is string {
  return typeof v === 'string';
}
function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const REQUIRED: Check[] = [
  { path: 'tenantId', test: isNonEmptyString },
  { path: 'office.name', test: isNonEmptyString },
  { path: 'office.shortName', test: isNonEmptyString },
  { path: 'office.district', test: isNonEmptyString },
  { path: 'office.state', test: isNonEmptyString },
  { path: 'office.site', test: isNonEmptyString },
  { path: 'office.siteCode', test: isNonEmptyString },
  { path: 'office.issuingAuthority', test: isNonEmptyString },
  { path: 'office.address', test: isNonEmptyString },
  { path: 'office.phone', test: isString },
  { path: 'office.email', test: isString },
  { path: 'office.whatsapp', test: isString },
  { path: 'office.grievanceOfficer', test: isNonEmptyString },
  { path: 'office.emblem', test: isString },
  { path: 'product.name', test: isNonEmptyString },
  { path: 'product.cardName', test: isNonEmptyString },
  { path: 'product.tagline', test: isString },
  { path: 'product.publicUrl', test: isNonEmptyString },
  { path: 'product.languages', test: isStringArray },
  { path: 'product.defaultLanguage', test: isNonEmptyString },
  { path: 'season.key', test: isNonEmptyString },
  { path: 'season.label', test: isNonEmptyString },
  { path: 'season.validFrom', test: isNonEmptyString },
  { path: 'season.validTo', test: isNonEmptyString },
  { path: 'season.cardValidityDays', test: isNumber },
  { path: 'season.cardPrefix', test: isNonEmptyString },
  { path: 'theme.primary', test: isNonEmptyString },
  { path: 'theme.primaryStrong', test: isNonEmptyString },
  { path: 'theme.accent', test: isNonEmptyString },
  { path: 'theme.ink', test: isNonEmptyString },
  { path: 'rules.rulesetVersion', test: isNonEmptyString },
  { path: 'rules.nameMatchPass', test: isNumber },
  { path: 'rules.nameMatchWarn', test: isNumber },
  { path: 'rules.recognisedCertBodies', test: isStringArray },
  { path: 'rules.certLevels', test: isStringArray },
  { path: 'rules.gliderClassMaxByLevel', test: isPlainObject },
  { path: 'rules.insuranceCoverPhrases', test: isStringArray },
  { path: 'keys', test: (v) => Array.isArray(v) },
  { path: 'retention.applicationYears', test: isNumber },
  { path: 'retention.documentYearsAfterExpiry', test: isNumber },
  { path: 'retention.scanLogYears', test: isNumber },
  { path: 'verify.revocationListPath', test: isNonEmptyString },
  { path: 'verify.staleAfterHours', test: isNumber },
];

function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => (isPlainObject(acc) ? acc[key] : undefined), obj);
}

/** Validate `json` against the required `TenantConfig` shape; throws a readable error listing every missing/invalid path. */
export function loadTenant(json: unknown): TenantConfig {
  const missing: string[] = [];
  for (const { path, test } of REQUIRED) {
    if (!test(getPath(json, path))) missing.push(path);
  }
  const keys = isPlainObject(json) ? json.keys : undefined;
  if (Array.isArray(keys)) {
    keys.forEach((k, i) => {
      for (const field of ['kid', 'publicKey', 'validFrom', 'validTo']) {
        if (!isNonEmptyString(isPlainObject(k) ? k[field] : undefined)) missing.push(`keys[${i}].${field}`);
      }
    });
  }
  if (missing.length > 0) {
    throw new Error(`Invalid tenant config — missing or invalid: ${missing.join(', ')}`);
  }
  return json as TenantConfig;
}

/** The card's validity window for `today`: starts no earlier than the season, ends at the season unless `cardValidityDays` shortens it. */
export function cardValidity(t: TenantConfig, today: ISODate): { validFrom: ISODate; validTo: ISODate } {
  const validFrom = today > t.season.validFrom ? today : t.season.validFrom;
  const validTo =
    t.season.cardValidityDays > 0
      ? (() => {
          const byDays = addDays(validFrom, t.season.cardValidityDays);
          return byDays < t.season.validTo ? byDays : t.season.validTo;
        })()
      : t.season.validTo;
  return { validFrom, validTo };
}

/** The season year, derived from the first 4 characters of `season.validFrom`. */
export function seasonYear(t: TenantConfig): string {
  return t.season.validFrom.slice(0, 4);
}

/** Card number for sequence `seq`: `<prefix>-<seasonYear>-<seq padded to 6 digits>`. */
export function cardNo(t: TenantConfig, seq: number): string {
  return `${t.season.cardPrefix}-${seasonYear(t)}-${String(seq).padStart(6, '0')}`;
}
